
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useUser } from '@/hooks/use-user';
import { toast } from 'sonner';

export interface UserImage {
  id: string;
  user_id: string;
  store_id: string;
  filename: string;
  original_name: string;
  file_size: number;
  mime_type: string;
  storage_path: string;
  thumbnail_path?: string;
  width?: number;
  height?: number;
  tags: string[];
  folder: string;
  usage_count: number;
  created_at: string;
  updated_at: string;
}

export const useUserImages = (storeId?: string) => {
  const { user } = useUser();
  const [images, setImages] = useState<UserImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  const fetchImages = async () => {
    if (!user?.id || !storeId) return;

    try {
      const { data, error } = await supabase
        .from('user_images')
        .select('*')
        .eq('user_id', user.id)
        .eq('store_id', storeId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setImages(data || []);
    } catch (error) {
      console.error('Error fetching images:', error);
      toast.error('Erro ao carregar imagens');
    } finally {
      setLoading(false);
    }
  };

  const uploadImage = async (file: File, folder: string = 'default', tags: string[] = []) => {
    if (!user?.id || !storeId) throw new Error('User or store not found');

    setUploading(true);
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${user.id}/${Date.now()}.${fileExt}`;
      
      const { error: uploadError } = await supabase.storage
        .from('user-store-images')
        .upload(fileName, file);

      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage
        .from('user-store-images')
        .getPublicUrl(fileName);

      const { data, error } = await supabase
        .from('user_images')
        .insert({
          user_id: user.id,
          store_id: storeId,
          filename: fileName,
          original_name: file.name,
          file_size: file.size,
          mime_type: file.type,
          storage_path: urlData.publicUrl,
          folder,
          tags,
        })
        .select()
        .single();

      if (error) throw error;
      
      setImages(prev => [data, ...prev]);
      toast.success('Imagem enviada com sucesso!');
      return data;
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Erro ao enviar imagem');
      throw error;
    } finally {
      setUploading(false);
    }
  };

  const deleteImage = async (imageId: string) => {
    try {
      const image = images.find(img => img.id === imageId);
      if (!image) return;

      const { error: storageError } = await supabase.storage
        .from('user-store-images')
        .remove([image.filename]);

      if (storageError) throw storageError;

      const { error } = await supabase
        .from('user_images')
        .delete()
        .eq('id', imageId);

      if (error) throw error;
      
      setImages(prev => prev.filter(img => img.id !== imageId));
      toast.success('Imagem removida com sucesso!');
    } catch (error) {
      console.error('Error deleting image:', error);
      toast.error('Erro ao remover imagem');
    }
  };

  useEffect(() => {
    fetchImages();
  }, [user?.id, storeId]);

  return {
    images,
    loading,
    uploading,
    uploadImage,
    deleteImage,
    refetch: fetchImages,
  };
};
